import { addDays, startOfDay } from "date-fns";
// eslint-disable-next-line no-restricted-imports -- needed for timezone-aware date calculations
import { utcToZonedTime, zonedTimeToUtc } from "date-fns-tz";

interface BuildShiftTimesParams {
  /** Reference date — the day portion is extracted from this (in facility timezone) */
  startDate: Date;
  /** Source for start HH:MM (in facility timezone) */
  startTime: Date;
  /** Source for end HH:MM (in facility timezone) */
  endTime: Date;
  /** Facility timezone */
  timezone?: string;
}

interface BuildShiftTimesResult {
  startTime: Date;
  endTime: Date;
}

/**
 * Builds correct shift start/end Date objects by combining a date with time portions.
 *
 * Extracts the day from `startDate`, hours/minutes from `startTime` and `endTime`
 * (all in facility timezone), and produces full UTC Date objects.
 *
 * If end HH:MM < start HH:MM (shift crosses midnight), the end date is set to
 * the day after the start date.
 */
export function buildShiftTimes(params: BuildShiftTimesParams): BuildShiftTimesResult {
  const { startDate, startTime, endTime, timezone } = params;

  const toZoned = (date: Date) => (timezone ? utcToZonedTime(date, timezone) : date);
  const toUtc = (date: Date) => (timezone ? zonedTimeToUtc(date, timezone) : date);

  const zonedDate = startOfDay(toZoned(startDate));
  const zonedStart = toZoned(startTime);
  const zonedEnd = toZoned(endTime);

  const newZonedStart = new Date(zonedDate);
  newZonedStart.setHours(zonedStart.getHours(), zonedStart.getMinutes(), 0, 0);

  const startMinutes = zonedStart.getHours() * 60 + zonedStart.getMinutes();
  const endMinutes = zonedEnd.getHours() * 60 + zonedEnd.getMinutes();
  const crossesMidnight = endMinutes < startMinutes;

  const endDate = crossesMidnight ? addDays(zonedDate, 1) : zonedDate;
  const newZonedEnd = new Date(endDate);
  newZonedEnd.setHours(zonedEnd.getHours(), zonedEnd.getMinutes(), 0, 0);

  return {
    startTime: toUtc(newZonedStart),
    endTime: toUtc(newZonedEnd),
  };
}
